import { useSnackbarContext } from "@ameelio/ui";
import { useMutation } from "@apollo/client/react";
import { endOfDay } from "date-fns";
import { Suspense, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation } from "react-router-dom";
import { useSound } from "use-sound";
import NotFoundScreen from "../404";
import {
  CallCallStatus,
  CallParticipant,
  InstantReply,
  ParticipantType,
} from "../api/graphql";
import { useNullableCallToken } from "../lib/callToken.ts";
import getFrom from "../lib/getFrom";
import {
  ServerToClientEvents,
  useServerEvent,
} from "../lib/serverEventsProvider";
import useMayHearAudio from "../lib/useMayHearAudio";
import getInstantReplyContent from "../Messages/getInstantReplyContent";
import DialingScreen from "./DialingScreen";
import EndedCallScreen from "./EndedCallScreen.tsx";
import { EndVoiceCallDocument } from "./EndVoiceCall.generated";
import { sendToParent } from "./iframeMessaging.ts";
import LiveCall from "./LiveCall";
import VoiceCallRingTone from "./LiveCall/sounds/VoiceCallRingTone.mp3";
import { IsolatedCall } from "./utils.tsx";

type VoiceCallPhase =
  | "dialing"
  | "call"
  | "declined"
  | "ended"
  | "terminated"
  | "missed"
  | "missed-closed"
  | "connectedElsewhere"
  | "errored";

type Props = {
  allowRating?: boolean;
  allowTextFeedback?: boolean;
  call: IsolatedCall;
  currentParticipant: Pick<
    CallParticipant,
    "externalId" | "role" | "fullName" | "type"
  >;
};

export default function CallScreen({
  allowRating,
  allowTextFeedback,
  call,
  currentParticipant,
}: Props) {
  const { t } = useTranslation();
  const { callToken } = useNullableCallToken();
  const location = useLocation();
  const from = getFrom(location) || "/";
  const [phase, setPhase] = useState<VoiceCallPhase>("dialing");
  const setPhaseAndNotify = useCallback(
    (newPhase: VoiceCallPhase) => {
      if (!call.id || newPhase === phase) return;
      setPhase(newPhase);
      if (!["dialing", "call", "connectedElsewhere"].includes(newPhase)) {
        sendToParent("CALL_ENDED", { callId: call.id, reason: newPhase });
      }
    },
    [call.id, phase]
  );
  const [muted, setMuted] = useState(false);
  const [alerts, setAlerts] = useState(true);
  const [selfEndedCall, setSelfEndedCall] = useState<boolean>(false);
  const [peerEndedCall, setPeerEndedCall] = useState<boolean>(false);
  const [hasPeerEverJoined, setHasPeerEverJoined] = useState(false);
  const [alreadyClosingFromMissed, setAlreadyClosingFromMissed] =
    useState(false);

  // Ringtone data
  const mayHearAudio = useMayHearAudio();
  const [ringtonePlayable, setRingtonePlayable] = useState<boolean>(false);
  const [ringtonePlaying, setRingtonePlaying] = useState<boolean>(false);
  const [playRingtone, { stop }] = useSound(VoiceCallRingTone, {
    soundEnabled: mayHearAudio, // Same headset enforcement as remote call audio — see useMayHearAudio.
    interrupt: true, // Safety guard - `interrupt` ensures that if the sound starts again before it's
    // ended, it will truncate it. Otherwise, the sound can overlap.
    onplay: () => setRingtonePlaying(true),
    onend: () => setRingtonePlaying(false),
    // stop() (called below, on unmount, and on peer join) fires onstop, not
    // onend — without this, ringtonePlaying stays stuck true after any stop()
    // and the ringtone can never be restarted (e.g. headset replugged mid-call).
    onstop: () => setRingtonePlaying(false),
  });

  const snackbarContext = useSnackbarContext();

  const [endCallMutation, { loading: endingVoiceCall }] = useMutation(
    EndVoiceCallDocument,
    {
      ...(callToken
        ? {
            context: {
              headers: {
                "x-call-token": callToken,
              },
            },
          }
        : {}),
      variables: {
        input: {
          callId: call.id,
          // `userInitiated` is currently hard-coded to `true` below because onLeave is only called
          // from clicking the "End call" button, so we can safely assume user intent. If that
          // changes, revisit whether or not this needs to be dynamic
          userInitiated: true,
        },
      },
    }
  );

  const onLeave = useCallback(() => {
    // Set the local user as the party
    // responsible for ending the call
    setSelfEndedCall(true);
    // Call the mutation
    endCallMutation().then(() => {
      sendToParent("USER_LEFT", {
        callId: call.id,
        userId: currentParticipant.externalId,
      });
      if (hasPeerEverJoined) setPhase("ended");
      else setPhase("missed-closed");
    });
  }, [
    hasPeerEverJoined,
    endCallMutation,
    setSelfEndedCall,
    currentParticipant,
    call,
  ]);

  const onEnded = useCallback(() => {
    if (!["terminated", "connectedElsewhere"].includes(phase)) {
      if (hasPeerEverJoined) setPhaseAndNotify("ended");
      else setPhaseAndNotify("missed-closed");
    }
  }, [hasPeerEverJoined, phase, setPhaseAndNotify]);

  const visitors =
    call?.participants.filter((p) => p.type === ParticipantType.User) ?? [];
  const peerFirstName =
    visitors.length > 0 ? visitors[0].fullName : t("Your contact");

  // Informs the user that the call recipient never answered
  const informCallEvent = useCallback(
    (vcPhase: VoiceCallPhase, reason?: string) => {
      const firstName = {
        firstName: peerFirstName,
      };
      snackbarContext.alert(
        // instant reply included on a decline, type is warning
        vcPhase === "declined" && reason?.length ? "warning" : "info",
        vcPhase === "declined"
          ? reason?.length
            ? t('{{firstName}} declined the call: "{{reason}}"', {
                ...firstName,
                reason,
              })
            : t("{{firstName}} declined the call", firstName)
          : t("{{firstName}} did not answer", firstName)
      );
    },
    [snackbarContext, t, peerFirstName]
  );

  // Used to play a ringtone to indicate to the
  // user their call is outgoing. Stops whenever
  // the phase is not "call", or audio is disallowed
  // (headset unplugged in the residents-app WebView).
  useEffect(() => {
    if (
      phase === "call" &&
      !hasPeerEverJoined &&
      ringtonePlayable &&
      mayHearAudio
    ) {
      if (!ringtonePlaying) {
        // Note the `onplay` callback defined
        // above handles setting ringtonePlaying,
        // which is why we're not setting in state here.
        // Looping is accomplished automatically because
        // when the track ends, it will call `onend` which
        // will set ringtonePlaying to false, allowing the
        // play function to be invoked anew
        playRingtone();
      }
    } else if (ringtonePlaying) {
      // Otherwise stop if playing. soundEnabled alone wouldn't cut off audio
      // already mid-loop when mayHearAudio flips false, so stop explicitly.
      stop();
    }
    // Clean-up
    return () => {
      if (ringtonePlaying) stop();
    };
  }, [
    phase,
    hasPeerEverJoined,
    playRingtone,
    stop,
    ringtonePlaying,
    ringtonePlayable,
    mayHearAudio,
  ]);

  // To better inform the user, shows a snackbar when the call phase
  // is set to missed or missed-closed to inform the user that call
  // recipient never answered
  useEffect(() => {
    if (["missed", "missed-closed"].includes(phase)) {
      informCallEvent(phase);
    }
  }, [phase, informCallEvent]);

  const onDeclined = useCallback(
    ({
      callId: declinedCallId,
      reason,
    }: ServerToClientEvents["voiceCallDeclined"]) => {
      if (declinedCallId === call.id && phase === "call") {
        informCallEvent(
          "declined",
          reason?.length
            ? getInstantReplyContent(t, reason.toUpperCase() as InstantReply)
            : undefined
        );
        setPhaseAndNotify("declined");
      }
    },
    [call.id, phase, informCallEvent, t, setPhaseAndNotify]
  );

  useServerEvent("voiceCallDeclined", onDeclined);

  useServerEvent(
    "voiceCallEnded",
    useCallback(
      ({ callId, userInitiated }: ServerToClientEvents["voiceCallEnded"]) => {
        if (call.id === callId) {
          if (userInitiated) setPeerEndedCall(true);
          onEnded();
        }
      },
      [call.id, onEnded]
    )
  );

  useEffect(() => {
    if (call.callStatus === CallCallStatus.Live && phase === "dialing")
      setPhaseAndNotify("call");
    if (
      call.callStatus === CallCallStatus.Ended &&
      Date.now() < endOfDay(call.endedAt).getTime() // This is end of day local time: https://date-fns.org/docs/endOfDay
    ) {
      setPhaseAndNotify("ended");
    }
  }, [call, phase, setPhaseAndNotify]);

  // used to alert the user and leave the voice
  // call screen if there is an error creating the room
  const onInitializationFailed = useCallback(() => {
    setPhaseAndNotify("errored");
    snackbarContext.alert(
      "warning",
      t(
        "Sorry, we could not initialize this call. Please wait a moment and try again."
      )
    );
  }, [snackbarContext, t, setPhaseAndNotify]);

  // listens for an error initializing the voice call
  // and invokes a callback to alert/leave the call screen
  useServerEvent(
    "voiceCallInitializationFailed",
    useCallback(
      ({ callId }: ServerToClientEvents["voiceCallInitializationFailed"]) => {
        if (callId === call.id) onInitializationFailed();
      },
      [call.id, onInitializationFailed]
    )
  );

  // used as a fail-safe to leave the voice call screen if an unexpected error
  // occurs for which this client isn't notified
  useEffect(() => {
    const leaveFromError = setTimeout(() => {
      // if the phase never progressed past dialing after 45 seconds,
      // assume an error, alert the user, and navigate back
      if (phase === "dialing") onInitializationFailed();
    }, 45000);
    return () => clearTimeout(leaveFromError);
  }, [onInitializationFailed, phase]);

  useEffect(() => {
    if (phase === "missed" && !alreadyClosingFromMissed) {
      setTimeout(() => {
        setPhaseAndNotify("missed-closed");
      }, 2000);
      setAlreadyClosingFromMissed(true);
    }
  }, [
    phase,
    setPhaseAndNotify,
    alreadyClosingFromMissed,
    setAlreadyClosingFromMissed,
  ]);

  if (
    ![CallCallStatus.Scheduled, CallCallStatus.Live].includes(
      call.callStatus
    ) &&
    phase !== "ended" // phase is 'ended' if we already determined that the call ended today.
  ) {
    return <NotFoundScreen />;
  }

  let children: React.ReactNode;
  if (phase === "dialing") {
    children = (
      <DialingScreen
        currentParticipant={currentParticipant}
        muted={muted}
        alerts={alerts}
        onLeave={onLeave}
        leaving={endingVoiceCall}
        onToggleAlerts={() => setAlerts((a) => !a)}
        onToggleAudio={() => setMuted((m) => !m)}
        call={call}
        missed={false}
      />
    );
  } else if (phase === "call") {
    children = (
      <Suspense
        fallback={
          <DialingScreen
            currentParticipant={currentParticipant}
            muted={muted}
            alerts={alerts}
            onLeave={onLeave}
            leaving={endingVoiceCall}
            onToggleAudio={() => setMuted((m) => !m)}
            onToggleAlerts={() => setAlerts((a) => !a)}
            call={call}
            missed={false}
          />
        }
      >
        <LiveCall
          currentParticipant={currentParticipant}
          call={call}
          onLeave={onLeave}
          leaving={endingVoiceCall}
          beginHidden={false}
          beginMuted={muted}
          alerts={alerts}
          onToggleAlerts={() => setAlerts((a) => !a)}
          onPeerJoined={() => {
            if (ringtonePlaying) stop();
            setHasPeerEverJoined(true);
          }}
          onTerminated={() => setPhaseAndNotify("terminated")}
          onConnectedElsewhere={() => setPhaseAndNotify("connectedElsewhere")}
          onAudioReady={() => setRingtonePlayable(true)}
          onEnded={onEnded}
        />
      </Suspense>
    );
  } else if (phase === "missed") {
    children = (
      <DialingScreen
        currentParticipant={currentParticipant}
        muted={muted}
        alerts={alerts}
        leaving={endingVoiceCall}
        onToggleAudio={() => {}}
        onToggleAlerts={() => {}}
        call={call}
        onLeave={() => {}}
        missed
      />
    );
  } else if (["missed-closed", "declined", "errored"].includes(phase)) {
    return <Navigate to={from} />;
  } else {
    children = (
      <EndedCallScreen
        currentParticipant={currentParticipant}
        call={call}
        title={
          selfEndedCall
            ? t("You ended the call")
            : peerEndedCall
              ? t("{{firstName}} ended the call", {
                  firstName: peerFirstName,
                })
              : t("Voice call ended")
        }
        collectRating={!!allowRating && hasPeerEverJoined}
        terminated={phase === "terminated"}
        connectedElsewhere={phase === "connectedElsewhere"}
        allowTextFeedback={allowTextFeedback}
      />
    );
  }

  return children;
}
